import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import IntegrationsSection from "../components/IntegrationsSection";
import HowItWorksSection from "../components/HowItWorksSection";
import MarketplaceSection from "../components/MarketplaceSection";
import BlogCard from "../components/BlogCard";
import CaseStudyCard from "../components/CaseStudyCard";
import SEOHead from "../components/SEOHead";
import { organizationSchema, websiteSchema, landingFaqPageSchema } from "../data/structuredData";
import UrgencyBar from "../components/UrgencyBar";
import StickyMobileCTA from "../components/StickyMobileCTA";
import LogosSection from "../components/LogosSection";
import { GuaranteeSection } from "../components/GuaranteeSection";
import VideoTestimonialSection from "../components/VideoTestimonialSection";
import { api } from "../lib/api";
import { ArrowRight, Zap, Brain, Cpu, Cloud, Shield, Workflow, ChevronDown, ChevronUp } from "lucide-react";

const FEATURES = [
  { icon: Zap, k: "01", titleKey: "landing.features.f1_title", descKey: "landing.features.f1_desc" },
  { icon: Workflow, k: "02", titleKey: "landing.features.f2_title", descKey: "landing.features.f2_desc" },
  { icon: Brain, k: "03", titleKey: "landing.features.f3_title", descKey: "landing.features.f3_desc" },
  { icon: Cpu, k: "04", titleKey: "landing.features.f4_title", descKey: "landing.features.f4_desc" },
  { icon: Shield, k: "05", titleKey: "landing.features.f5_title", descKey: "landing.features.f5_desc" },
  { icon: Cloud, k: "06", titleKey: "landing.features.f6_title", descKey: "landing.features.f6_desc" },
];

const TESTIMONIALS = [
  { name: "James Mitchell", role: "Operations Manager, SaaS Co.", quote: "We deployed Max OS Cloud and eliminated 15 hours of weekly data entry. The setup was seamless.", img: "https://images.unsplash.com/photo-1614010966237-74489a16848b?crop=entropy&cs=srgb&fm=jpg&q=85&w=200&h=200&fit=crop" },
  { name: "Rebecca Santos", role: "Freelance Designer", quote: "Max Employee handles my email and calendar 24/7. I've reclaimed 8 hours per week for client work.", img: "https://images.unsplash.com/photo-1611111082664-534b2cd71d6f?crop=entropy&cs=srgb&fm=jpg&q=85&w=200&h=200&fit=crop" },
  { name: "David Park", role: "Founder, E-commerce", quote: "We started with the Playbook and scaled to Max OS Cloud within two weeks. Response times dropped 60%.", img: "https://images.unsplash.com/photo-1556157382-97eda2d62296?crop=entropy&cs=srgb&fm=jpg&q=85&w=200&h=200&fit=crop" },
];

const FAQ_ITEMS = [
  { qKey: "landing.faq.q1", aKey: "landing.faq.a1" },
  { qKey: "landing.faq.q2", aKey: "landing.faq.a2" },
  { qKey: "landing.faq.q3", aKey: "landing.faq.a3" },
  { qKey: "landing.faq.q4", aKey: "landing.faq.a4" },
  { qKey: "landing.faq.q5", aKey: "landing.faq.a5" },
  { qKey: "landing.faq.q6", aKey: "landing.faq.a6" },
  { qKey: "landing.faq.q7", aKey: "landing.faq.a7" },
  { qKey: "landing.faq.q8", aKey: "landing.faq.a8" },
  { qKey: "landing.faq.q9", aKey: "landing.faq.a9" },
  { qKey: "landing.faq.q10", aKey: "landing.faq.a10" },
];

function VideoPlayer() {
  const { t } = useTranslation();

  return (
    <div className="relative w-full aspect-video rounded-lg overflow-hidden group">
      <video
        className="w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        poster="/demo-video-poster.webp"
        aria-label={t('landing.max_employee_demo_video')}
        title={t('landing.max_employee_demo_video')}
      >
        <source src="/demo-video.mp4" type="video/mp4" />
      </video>
      {/* Subtle gradient overlay at bottom for readability */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

    </div>
  );
}

function FAQItem({ item, isOpen, onToggle, t }) {
  const qText = t(item.qKey);
  const btnId = `faq-btn-${qText.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "")}`;
  const panelId = `faq-panel-${qText.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "")}`;
  return (
    <div className="border-b border-zinc-800">
      <button
        id={btnId}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="w-full flex items-center justify-between py-4 sm:py-5 text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B] rounded-sm min-h-[44px]"
      >
        <span className="text-white text-sm font-medium pr-4">{qText}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-amber-500 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" aria-hidden="true" />
        )}
      </button>
      {isOpen && (
        <div id={panelId} role="region" aria-labelledby={btnId}>
          <p className="text-zinc-400 text-sm leading-relaxed pb-5 pr-8">{t(item.aKey)}</p>
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentCases, setRecentCases] = useState([]);

  const toggleFaq = (idx) => { setOpenFaq((prev) => (prev === idx ? null : idx)); };

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  // Defer below-the-fold data: load only when blog section is visible
  useEffect(() => {
    const blogSection = document.getElementById('blog-section-observer');
    if (!blogSection) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          import("../data/blogPosts").then(({ getRecentPosts }) => {
            setRecentPosts(getRecentPosts(3));
          }).catch(() => {});
          import("../data/caseStudies").then(({ getRecentCaseStudies }) => {
            setRecentCases(getRecentCaseStudies(2));
          }).catch(() => {});
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(blogSection);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={t('landing.max_os_cloud_ai_digital_workers_for_business_max')}
        description="Deploy autonomous AI digital workers for your business. 24/7 operation, multi-channel, custom skills. Start your 7-day free trial — cancel anytime."
        image="/og-image-homepage.png"
        url="/"
        structuredData={[organizationSchema, websiteSchema, landingFaqPageSchema]}
      />
      <Header />
      <UrgencyBar />

      <main id="main-content">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-900" aria-labelledby="hero-heading">
        <div className="absolute inset-0 grid-bg opacity-60" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#09090B]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 sm:pt-20 pb-16 sm:pb-32">
          {/* System label */}
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-8 cursor-blink" aria-hidden="true">
            {t('landing.hero.system_label')}
          </div>

          {/* Two-column layout: text left, video right (desktop) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Headline + CTA */}
            <div>
              <h1 id="hero-heading" className="font-display text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl uppercase leading-[0.9]">
                {t('landing.hero.title_1')}<br/>
                {t('landing.hero.title_2')}<br/>
                <span className="text-amber-500">{t('landing.hero.title_3')}</span> {t('landing.hero.title_4')}
              </h1>
              <p className="mt-8 max-w-xl text-lg text-zinc-400 leading-relaxed">
                {t('landing.hero.subtitle')}
              </p>
                <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to="/register"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center justify-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-6 sm:px-8 py-3.5 sm:py-5 font-bold uppercase tracking-wider text-sm transition-all hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B] min-h-[44px]"
                >
                  {t('landing.hero.cta_primary')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform animate-pulse" aria-hidden="true" />
                </Link>
                <a
                  href="/pricing"
                  data-testid="hero-cta-pricing"
                  className="inline-flex items-center justify-center gap-3 border border-zinc-800 hover:border-zinc-500 text-white px-6 sm:px-8 py-3.5 sm:py-5 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B] min-h-[44px]"
                >
                  {t('landing.hero.cta_secondary')}
                </a>
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400" aria-hidden="true">
                {t('landing.hero.trial_note')}
              </p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-zinc-300">
                {t('landing.hero.billing_note')}
              </p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-amber-500">
                {t('landing.hero.social_proof')}
              </p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                <span aria-hidden="true">📊</span> {t('landing.hero.weekly_activation')}
              </p>
            </div>

            {/* Right: Video placeholder (hidden on mobile, shown on lg+) */}
            <div className="hidden lg:block">
              <VideoPlayer />
            </div>
          </div>

          {/* Mobile: Video placeholder below headline, above stats */}
          <div className="mt-12 lg:hidden">
            <VideoPlayer />
          </div>

          {/* Social proof stats bar */}
          <div className="mt-24 border-t border-zinc-900" role="group" aria-label={t('landing.key_statistics')}>
            <div className="grid grid-cols-2 md:grid-cols-4">
              {[
                { value: "1,652", labelKey: "landing.stats.hours_saved" },
                { value: "33", labelKey: "landing.stats.businesses" },
                { value: "24/7", labelKey: "landing.stats.uptime" },
                { value: "5", labelKey: "landing.stats.industries" },
              ].map(({ value, labelKey }) => (
                <div key={labelKey} className="border-r last:border-r-0 border-zinc-900 py-8 px-6">
                  <div className="font-display text-2xl sm:text-4xl md:text-5xl text-white uppercase" aria-hidden="true">{value}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-2">{t(labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Social proof urgency bar */}
          <div className="mt-16 border border-zinc-800 rounded-lg px-6 py-4 flex items-center justify-center gap-3 bg-zinc-950/50">
            <span className="text-amber-500 text-lg" aria-hidden="true">⚡</span>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300">
              {t('landing.urgency.setup_time')}
            </p>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden border-b border-zinc-900 bg-black py-6" aria-hidden="true">
        <div className="marquee whitespace-nowrap font-display text-2xl text-zinc-700 uppercase tracking-tight flex gap-12">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-12 shrink-0">
              {["AUTONOMOUS", "★", "PROACTIVE", "★", "24/7", "★", "SELF-LEARNING", "★", "ENTERPRISE-GRADE", "★", "VPS-READY", "★"].map((w, j) => (
                <span key={j}>{w}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* INTEGRATIONS — moved up to sit right below the stats grid */}
      <IntegrationsSection />

      {/* HOW IT WORKS */}
      <HowItWorksSection />

      {/* CAPABILITIES (0,2) — moved up right below How It Works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-24" aria-labelledby="features-heading">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4" aria-hidden="true">{t('landing.features.section_label')}</div>
            <h2 id="features-heading" className="font-display text-2xl sm:text-4xl md:text-5xl uppercase leading-tight">{t('landing.features.title_1')}<br/>{t('landing.features.title_2')}</h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <p className="text-zinc-400 text-lg leading-relaxed">
              {t('landing.features.subtitle')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-zinc-900">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.k} className="border-r border-b border-zinc-900 p-8 hover:bg-zinc-950 transition-colors">
                <div className="flex items-center justify-between mb-6">
                  <Icon className="w-6 h-6 text-amber-500" aria-hidden="true" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" aria-hidden="true">{f.k}</span>
                </div>
                <h3 className="font-display text-xl uppercase mb-3">{t(f.titleKey)}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{t(f.descKey)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* AGENT MARKETPLACE */}
      <MarketplaceSection />

      {/* GUARANTEE */}
      <GuaranteeSection />

      {/* LOGOS — Trusted By */}
      <LogosSection />

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-24" aria-labelledby="testimonials-heading">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4" aria-hidden="true">{t('landing.testimonials.section_label')}</div>
        <h2 id="testimonials-heading" className="font-display text-2xl sm:text-4xl md:text-5xl uppercase leading-tight mb-16">{t('landing.testimonials.title_1')}<br/>{t('landing.testimonials.title_2')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900">
          {TESTIMONIALS.map((tItem) => (
            <div key={tItem.name} className="bg-[#09090B] p-8">
              <p className="text-zinc-200 leading-relaxed text-lg">&ldquo;{tItem.quote}&rdquo;</p>
              <div className="mt-8 flex items-center gap-4 pt-6 border-t border-zinc-900">
                <img src={tItem.img} alt={`Photo of ${tItem.name}, ${tItem.role}`} className="w-10 h-10 grayscale object-cover" loading="lazy" decoding="async" />
                <div>
                  <div className="font-display text-sm uppercase">{tItem.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">{tItem.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* VIDEO TESTIMONIALS */}
      <VideoTestimonialSection />

      {/* CASE STUDIES */}
      <section className="border-t border-zinc-900 bg-black" aria-labelledby="cases-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4" aria-hidden="true">{t('landing.cases.section_label')}</div>
              <h2 id="cases-heading" className="font-display text-2xl sm:text-4xl md:text-5xl uppercase leading-tight">
                {t('landing.cases.title_1')}<br/><span className="text-amber-500">{t('landing.cases.title_2')}</span>
              </h2>
            </div>
            <Link
              to="/cases"
              className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              {t('landing.cases.view_all')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {recentCases.map((cs) => (
              <CaseStudyCard key={cs.slug} caseStudy={cs} />
            ))}
          </div>
          <div className="mt-8 sm:hidden text-center">
            <Link
              to="/cases"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              {t('landing.cases.view_all_studies')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-zinc-900 bg-black" aria-labelledby="final-cta-heading">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-6" aria-hidden="true">{t('landing.final_cta.section_label')}</div>
          <h2 id="final-cta-heading" className="font-display text-2xl sm:text-4xl md:text-6xl uppercase leading-[0.95] max-w-3xl mx-auto">
            {t('landing.final_cta.title_1')}<br/>{t('landing.final_cta.title_2')}<br/><span className="text-amber-500">{t('landing.final_cta.title_3')}</span>
          </h2>
          <Link
            to="/register"
            data-testid="final-cta-primary"
            className="mt-10 inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-10 py-5 font-bold uppercase tracking-wider text-sm transition-all hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            {t('landing.hero.cta_primary')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            {t('landing.final_cta.note')}
          </p>
        </div>
      </section>

      {/* LATEST FROM THE BLOG */}
      <section id="blog-section-observer" className="border-t border-zinc-900 bg-[#09090B]" aria-labelledby="blog-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4" aria-hidden="true">{t('landing.blog.section_label')}</div>
              <h2 id="blog-heading" className="font-display text-2xl sm:text-4xl md:text-5xl uppercase leading-tight">
                {t('landing.blog.title_1')}<br/><span className="text-amber-500">{t('landing.blog.title_2')}</span>
              </h2>
            </div>
            <Link
              to="/blog"
              className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              {t('landing.blog.view_all')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentPosts.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
          <div className="mt-8 sm:hidden text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              {t('landing.blog.view_all_articles')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-20" aria-labelledby="faq-heading">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4" aria-hidden="true">// FAQ</div>
        <h2 id="faq-heading" className="font-display text-2xl sm:text-4xl md:text-5xl uppercase leading-tight mb-16">
          {t('landing.faq.title_1')}<br />{t('landing.faq.title_2')}
        </h2>
        <div className="border-t border-zinc-800">
          {FAQ_ITEMS.map((item, idx) => (
            <FAQItem
              key={idx}
              item={item}
              isOpen={openFaq === idx}
              onToggle={() => toggleFaq(idx)}
              t={t}
            />
          ))}
        </div>
      </section>
      </main>
      <StickyMobileCTA />
      <Footer />
    </div>
  );
}
